// Startseite „Start“ (design-system §5.1): ein Verteiler, kein Endpunkt. Links oben „Zu erledigen“ (das Einzige,
// was Handlung auslöst), daneben die Bausteine der Module aus ihren Manifesten (widgets) im Raster.
// Ohne Bausteine bleibt die Seite ruhig und sagt, was hier entsteht.
import { ArrowRight, Check, Search } from 'lucide-react'
import { lazy, Suspense, useMemo, type ComponentType } from 'react'
import { formatDate } from '@shared/format'
import { berlinParts, businessDate } from '@shared/time'
import { taskList } from '@modules/platform/contract'
import { useWebModules } from '../api/context'
import { SectionErrorBoundary } from '../api/pages/states'
import { canRunAction, passesGate } from '../api/permissions'
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import type { DashboardWidget } from '../module'
import { shell } from '../stores/shell'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { DateText } from '../ui/format'
import { Kbd } from '../ui/Kbd'
import { Skeleton } from '../ui/Skeleton'
import { useOpenTasks } from './NotificationCenter'

// Das Raster folgt der Breite der Arbeitsfläche (Container), nicht der des Fensters: Mit angedocktem Assistenten
// oder schmaler Sidebar-Schiene bleibt jede Karte lesbar.
const GRID = 'grid grid-cols-1 gap-4 @3xl:grid-cols-2 @6xl:grid-cols-4'
const SPAN: Record<NonNullable<DashboardWidget['size']>, string> = {
  sm: '',
  md: '@6xl:col-span-2',
  lg: '@3xl:col-span-2 @6xl:col-span-4',
}
const HALF = '@6xl:col-span-2'
const FULL = '@3xl:col-span-2 @6xl:col-span-4'

const card = 'flex min-w-0 flex-col rounded-md border border-line bg-surface'

function greeting(now: Date): string {
  const { hour } = berlinParts(now)
  return hour < 11 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend'
}

const widgetComponents = new Map<string, ComponentType>()
function componentOf(widget: DashboardWidget): ComponentType {
  let component = widgetComponents.get(widget.id)
  if (!component) {
    component = lazy(widget.lazy)
    widgetComponents.set(widget.id, component)
  }
  return component
}

function WidgetCard({ widget }: { widget: DashboardWidget }) {
  const Content = componentOf(widget)
  return (
    <section className={cn(card, SPAN[widget.size ?? 'md'])} data-ui="card" aria-label={widget.title}>
      <h2 className="px-4 pt-3 text-dense font-semibold text-fg touch:text-body">{widget.title}</h2>
      <div className="min-h-24 flex-1 px-4 pt-2 pb-4">
        <SectionErrorBoundary label={`Der Baustein „${widget.title}“`}>
          <Suspense fallback={<Skeleton variant="text" lines={3} />}>
            <Content />
          </Suspense>
        </SectionErrorBoundary>
      </div>
    </section>
  )
}

/** „Zu erledigen“: die dringendsten offenen Aufgaben der Standorte, an denen der Nutzer Aufgaben sehen darf. */
function TasksCard({ className }: { className?: string }) {
  const session = useSession()
  const tasks = useOpenTasks({ limit: 6 })
  const rows = tasks.data?.rows ?? []
  const today = businessDate()
  return (
    <section className={cn(card, className)} data-ui="card" aria-label="Zu erledigen" data-testid="start-tasks">
      <header className="flex min-h-10 items-center gap-2 px-4 pt-2">
        <h2 className="flex-1 text-dense font-semibold text-fg touch:text-body">Zu erledigen</h2>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={() => shell.openNotifications('tasks')}
            className="-mr-2 flex items-center gap-1 rounded-sm px-2 py-1 text-dense text-fg-muted transition-colors duration-(--duration-instant) hover:bg-hover hover:text-fg touch:min-h-11"
          >
            Alle Aufgaben
            <ArrowRight className="size-4" strokeWidth={1.75} aria-hidden />
          </button>
        )}
      </header>
      <div className="flex-1 px-4 pt-1 pb-3">
        {tasks.isPending ? (
          <Skeleton variant="text" lines={3} />
        ) : tasks.error ? (
          <p className="text-dense text-fg-muted">
            Die Aufgaben konnten nicht geladen werden.{' '}
            <button
              type="button"
              className="hit-y text-fg-accent underline-offset-2 hover:underline"
              onClick={() => void tasks.refetch()}
            >
              Erneut versuchen
            </button>
          </p>
        ) : rows.length === 0 ? (
          <div className="flex items-center gap-3 py-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sunken text-success-fg">
              <Check className="size-4" strokeWidth={2} aria-hidden />
            </span>
            <div>
              <p className="text-dense font-medium text-fg">Nichts zu erledigen</p>
              <p className="text-dense text-fg-muted">
                Erwartete Wareneingänge, Umlagerungen im Transit und Zählaufträge erscheinen hier von selbst.
              </p>
            </div>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-line-subtle">
            {rows.map((task) => {
              const location = session?.locations.find((entry) => entry.id === task.locationId)
              const overdue = task.dueDate !== null && task.dueDate < today
              return (
                <li key={task.id}>
                  <button
                    type="button"
                    onClick={() => shell.openNotifications('tasks')}
                    className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-sm px-2 py-2.5 text-left transition-colors duration-(--duration-instant) hover:bg-hover focus-inset touch:min-h-12 touch:py-3"
                  >
                    <span
                      className={cn(
                        'mt-[0.4375rem] size-1.5 shrink-0 rounded-full touch:mt-2.5',
                        overdue ? 'bg-danger' : 'bg-fg-subtle',
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-dense text-fg touch:text-touch">{task.title}</span>
                      {task.body && (
                        <span className="mt-0.5 line-clamp-2 text-caption text-fg-muted touch:text-dense">
                          {task.body}
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2 pt-px touch:pt-1">
                      {location && <Badge size="sm">{location.code}</Badge>}
                      {task.dueDate && (
                        <span className={cn('text-caption', overdue ? 'text-danger-fg' : 'text-fg-subtle')}>
                          {overdue && <span className="sr-only">überfällig seit </span>}
                          <DateText value={task.dueDate} short />
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

/** Ruhiger Platzhalter, solange kein Modul Bausteine liefert. */
function NoWidgets({ className }: { className?: string }) {
  return (
    <section className={cn(card, 'gap-2 px-4 pt-3 pb-4', className)} data-ui="card" data-testid="start-empty">
      <h2 className="flex min-h-7 items-center text-dense font-semibold text-fg touch:text-body">
        Ihr Tagesüberblick entsteht hier
      </h2>
      <p className="max-w-prose text-dense text-fg-muted">
        Kennzahlen, Verlauf und Vergleiche erscheinen, sobald die Bereiche für Lager, Einkauf und Verkauf eingerichtet
        sind. Bis dahin führt die Suche zu jeder Seite und jedem Befehl.
      </p>
      <div className="pt-1">
        <Button size="sm" icon={Search} onClick={() => shell.openPalette()}>
          <span className="flex items-center gap-2">
            Suchen <Kbd hotkey="mod+k" variant="plain" className="max-md:hidden touch:hidden" />
          </span>
        </Button>
      </div>
    </section>
  )
}

export function StartPage() {
  const session = useSession()
  const modules = useWebModules()
  const widgets = useMemo(
    () =>
      modules
        .flatMap((module) => module.widgets)
        .filter((widget) => passesGate(session, widget, 'any'))
        .sort((a, b) => (a.order ?? 500) - (b.order ?? 500)),
    [modules, session],
  )
  if (!session) return null
  const showTasks = canRunAction(session, taskList, 'any')
  const location = session.locations.find((entry) => entry.id === session.activeLocationId)
  const firstName = session.user.displayName.split(/\s+/)[0] ?? session.user.displayName
  const now = new Date()

  return (
    <div className="flex flex-col gap-5 px-(--page-px) pt-5 pb-8 md:pt-6" data-testid="start-page">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-heading text-fg">
          {greeting(now)}, {firstName}.
        </h1>
        <p className="text-body text-fg-muted">{formatDate(businessDate(now), { weekday: true })}</p>
        <p className="ml-auto text-dense text-fg-muted max-md:hidden">
          {location ? `${location.code} ${location.name}` : 'Alle Standorte'}
        </p>
      </header>
      <div className="@container">
        <div className={GRID}>
          {showTasks && <TasksCard className={HALF} />}
          {widgets.map((widget) => (
            <WidgetCard key={widget.id} widget={widget} />
          ))}
          {widgets.length === 0 && <NoWidgets className={showTasks ? HALF : FULL} />}
        </div>
      </div>
    </div>
  )
}
